import { baseApi } from "./api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

const BUSINESS_LINES_ROUTES = {
  GET_BUSINESS_LINES: "/business-lines/",
  GET_BUSINESS_LINE_ID: "/business-lines/:id",
  GET_CALL_FLOW_FORM: "/business-lines/:id/form?line_status=active",
  GET_DEFAULT_FORM_FIELDS: "/business-lines/form/default-form-fields",
  GET_DEFAULT_CLOSURES: "/default-closures",
  UPDATE_BUSINESS_HOUR_DATA:
    "/business-lines/:id/business-hour-data?line_status=active",
  GET_QA: "/business-lines/:id/qa?line_status=active",
};

export interface IFormField {
  required: boolean;
  verification_required: boolean;
  field_type:
    | "text"
    | "number"
    | "email"
    | "tel"
    | "radio"
    | "checkbox"
    | "select";
  note: string | null;
  title: string;
  choices?: string[] | null;
  id?: string | null;
  is_default: boolean;
}

interface IShift {
  start: number;
  end: number;
}

interface IDaySchedule {
  shifts: IShift[];
  start: number;
  end: number;
}

export interface IClosure {
  title: string;
  start: string;
  end: string;
  id: string | null;
  is_default: boolean;
  business_line_id: string | null;
}

export interface IWorkingHours {
  fr: IDaySchedule | null;
  mo: IDaySchedule | null;
  sa: IDaySchedule | null;
  su: IDaySchedule | null;
  th: IDaySchedule | null;
  tu: IDaySchedule | null;
  we: IDaySchedule | null;
}

export interface IBusinessHourData {
  closures: IClosure[];
  round_clock: boolean;
  timezone: string;
  working_hours: IWorkingHours;
}

interface IBusinessLineNote {
  content: string | null;
  expires_at: string | null;
}

interface IBusinessLineProfile {
  business_name: string | null;
  location: string | null;
}

export interface IBusinessLine {
  id: string;
  business: string;
  business_hour_data: IBusinessHourData;
  is_default: boolean;
  line_type: "regular" | "toll_free";
  note: IBusinessLineNote;
  phone_number: string;
  profile: IBusinessLineProfile;
  recording_enabled: boolean;
  service_type: "answering";
  status:
    | "active"
    | "inactive"
    | "pending_activation"
    | "reserved"
    | "suspended";
  package_id?: string;
}

export interface IDefaultClosure {
  id: number;
  title: string;
  start: string;
  end: string;
  is_default: boolean;
}

export interface IQaSection {
  sequence_no: number;
  title: string;
  description: string;
  qa_list: {
    sequence_no: number;
    question: string;
    answer: string;
    note: string;
    id: string;
  }[];
  id: string;
}

export interface IQaResponse {
  qa_sections: IQaSection[];
}

export type IQa = IQaSection[];

// React Query hooks with integrated API calls
export function useActiveBusinessLines(line_status?: string) {
  return useQuery({
    queryKey: ["activeBusinessLines", line_status],
    queryFn: async () => {
      const url = line_status
        ? `${BUSINESS_LINES_ROUTES.GET_BUSINESS_LINES}?line_status=${line_status}`
        : BUSINESS_LINES_ROUTES.GET_BUSINESS_LINES;
      const response = await baseApi.get(url);
      return response.data.business_lines;
    },
  });
}

export function useCallFlowForm(
  lineId: string,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ["callFlowForm", lineId],
    queryFn: async () => {
      const response = await baseApi.get(
        BUSINESS_LINES_ROUTES.GET_CALL_FLOW_FORM.replace(":id", lineId)
      );
      return response.data;
    },
    enabled: options?.enabled !== false && !!lineId,
    retry: (failureCount, error: any) => {
      // Don't retry on 404 errors (resource not found)
      if (error?.response?.status === 404) {
        return false;
      }
      // Retry up to 3 times for other errors
      return failureCount < 3;
    },
    retryDelay: 1000, // 1 second delay between retries
  });
}

export function useSaveCallFlowForm() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ fields, lineId }: { fields: any; lineId: string }) => {
      const response = await baseApi.post(
        BUSINESS_LINES_ROUTES.GET_CALL_FLOW_FORM.replace(":id", lineId),
        { fields }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["callFlowForm"] });
    },
  });
}

export function useDefaultFormFields() {
  return useQuery({
    queryKey: ["defaultFormFields"],
    queryFn: async () => {
      const response = await baseApi.get(
        BUSINESS_LINES_ROUTES.GET_DEFAULT_FORM_FIELDS
      );
      return response.data;
    },
    retry: (failureCount, error: any) => {
      // Don't retry on 404 errors (resource not found)
      if (error?.response?.status === 404) {
        return false;
      }
      // Retry up to 3 times for other errors
      return failureCount < 3;
    },
    retryDelay: 1000, // 1 second delay between retries
  });
}

export function useDefaultClosures() {
  return useQuery({
    queryKey: ["defaultClosures"],
    queryFn: async () => {
      const response = await baseApi.get(
        BUSINESS_LINES_ROUTES.GET_DEFAULT_CLOSURES
      );
      return response.data;
    },
  });
}

export function useBusinessLineById(
  id: string,
  line_status: string,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ["businessLine", id, line_status],
    queryFn: async () => {
      const url = BUSINESS_LINES_ROUTES.GET_BUSINESS_LINE_ID.replace(
        ":id",
        id
      ).replace(":line_status", line_status);
      const response = await baseApi.get(url);
      return response.data;
    },
    enabled: options?.enabled !== false && !!id,
    retry: (failureCount, error: any) => {
      // Don't retry on 404 errors (resource not found)
      if (error?.response?.status === 404) {
        return false;
      }
      // Retry up to 3 times for other errors
      return failureCount < 3;
    },
    retryDelay: 1000, // 1 second delay between retries
  });
}

export function useUpdateBusinessHourData() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const url = BUSINESS_LINES_ROUTES.UPDATE_BUSINESS_HOUR_DATA.replace(
        ":id",
        id
      );
      const response = await baseApi.put(url, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["businessLine"] });
    },
  });
}

export function useQa(id: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ["qa", id],
    queryFn: async () => {
      const url = BUSINESS_LINES_ROUTES.GET_QA.replace(":id", id);
      const response = await baseApi.get(url);
      return response.data;
    },
    enabled: options?.enabled !== false && !!id,
    retry: (failureCount, error: any) => {
      // Don't retry on 404 errors (resource not found)
      if (error?.response?.status === 404) {
        return false;
      }
      // Retry up to 3 times for other errors
      return failureCount < 3;
    },
    retryDelay: 1000, // 1 second delay between retries
  });
}

export function useUpdateQa() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const url = BUSINESS_LINES_ROUTES.GET_QA.replace(":id", id);
      const response = await baseApi.post(url, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["qa"] });
    },
  });
}

// React Query hooks object - organized like API functions
const businessLinesHooks = {
  // Query hooks (for fetching data)
  useActiveBusinessLines,
  useCallFlowForm,
  useDefaultFormFields,
  useDefaultClosures,
  useBusinessLineById,
  useQa,

  // Mutation hooks (for updating data)
  useSaveCallFlowForm,
  useUpdateQa,
  useUpdateBusinessHourData,
};

export { businessLinesHooks };
